import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  FaPlane,
  FaHotel,
  FaTrain,
  FaCar,
  FaPassport,
  FaPlus,
  FaChevronDown,
  FaChevronUp,
  FaEdit,
  FaTrash,
  FaShoppingCart,
  FaTimes,
  FaArrowLeft,
  FaUser,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaCheckCircle
} from 'react-icons/fa';
import FlightModal from '../components/modals/FlightModal';
import HotelModal from '../components/modals/HotelModal';
import TrainModal from '../components/modals/TrainModal';
import CarModal from '../components/modals/CarModal';
import VisaModal from '../components/modals/VisaModal';
import DeleteModal from '../components/modals/DeleteModal';

const BookingManagement = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const bookingId = searchParams.get('bookingId');

  const [expandedSection, setExpandedSection] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState('Select Customer');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [isEmployeeSelected, setIsEmployeeSelected] = useState(bookingId ? true : false);

  // Sample customers
  const customers = [
    {
      id: 'CUST001',
      name: 'John Smith',
      email: 'john.smith@company.com',
      phone: '+1 (555) 123-4567',
      address: '123 Business Ave, New York, NY 10001'
    },
    {
      id: 'CUST002',
      name: 'Sarah Johnson',
      email: 'sarah.johnson@enterprise.com',
      phone: '+1 (555) 234-5678',
      address: '456 Corporate Blvd, Los Angeles, CA 90210'
    },
    {
      id: 'CUST003',
      name: 'Michael Brown',
      email: 'michael.brown@business.com',
      phone: '+1 (555) 345-6789',
      address: '789 Executive St, Chicago, IL 60601'
    }
  ];

  const [customerData, setCustomerData] = useState(() => {
    if (bookingId) {
      // Load existing booking data
      const existingBookings = JSON.parse(localStorage.getItem('flightsBookings') || '[]');
      const existingBooking = existingBookings.find(booking => booking.id === bookingId);
      if (existingBooking) {
        return existingBooking.customer;
      }
      return customers[0]; // Fallback to demo customer
    } else {
      const nextId = JSON.parse(localStorage.getItem('flightsBookings') || '[]').length + 1;
      return {
        id: `CUST${String(nextId).padStart(3, '0')}`,
        name: '',
        email: '',
        phone: '',
        address: ''
      };
    }
  });

  const [bookingData, setBookingData] = useState({
    flights: [],
    hotels: [],
    trains: [],
    cars: [],
    visas: []
  });

  const [modals, setModals] = useState({
    flights: false,
    hotels: false,
    trains: false,
    cars: false,
    visas: false
  });

  const [editingItem, setEditingItem] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, type: null, itemId: null, itemName: '' });
  const [alertModal, setAlertModal] = useState({ isOpen: false, title: '', message: '' });
  const [nextId, setNextId] = useState({
    flights: 1,
    hotels: 1,
    trains: 1,
    cars: 1,
    visas: 1
  });

  // Load existing booking data when editing
  useEffect(() => {
    if (bookingId) {
      const existingBookings = JSON.parse(localStorage.getItem('flightsBookings') || '[]');
      const existingBooking = existingBookings.find(booking => booking.id === bookingId);
      if (existingBooking) {
        setBookingData(existingBooking.bookings);
        // Update nextId based on existing items
        const newNextId = { ...nextId };
        Object.keys(existingBooking.bookings).forEach(type => {
          const maxId = existingBooking.bookings[type].reduce((max, item) =>
            Math.max(max, item.id || 0), 0
          );
          newNextId[type] = maxId + 1;
        });
        setNextId(newNextId);
      }
    }
  }, [bookingId]);

  const bookingSections = [
    {
      id: 'flights',
      title: 'Flight Reservations',
      icon: FaPlane,
      color: 'from-blue-500 to-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      items: bookingData.flights
    },
    {
      id: 'hotels',
      title: 'Hotel Accommodations',
      icon: FaHotel,
      color: 'from-green-500 to-green-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      items: bookingData.hotels
    },
    {
      id: 'trains',
      title: 'Train Services',
      icon: FaTrain,
      color: 'from-purple-500 to-purple-600',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-200',
      items: bookingData.trains
    },
    {
      id: 'cars',
      title: 'Car Rentals',
      icon: FaCar,
      color: 'from-orange-500 to-orange-600',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200',
      items: bookingData.cars
    },
    {
      id: 'visas',
      title: 'Visa Services',
      icon: FaPassport,
      color: 'from-red-500 to-red-600',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200',
      items: bookingData.visas
    }
  ];

  const handleCustomerSelect = (customer) => {
    if (customer === 'new') {
      const nextId = JSON.parse(localStorage.getItem('flightsBookings') || '[]').length + 1;
      setCustomerData({
        id: `CUST${String(nextId).padStart(3, '0')}`,
        name: '',
        email: '',
        phone: '',
        address: ''
      });
      setSelectedCustomer('New Customer');
      setIsEmployeeSelected(true);
    } else {
      setCustomerData(customer);
      setSelectedCustomer(customer.name);
      setIsEmployeeSelected(true);
    }
    setShowCustomerDropdown(false);
  };

  const openModal = (type) => {
    if (!isEmployeeSelected) return;
    setModals(prev => ({ ...prev, [type]: true }));
  };

  const closeModal = (type) => {
    setModals(prev => ({ ...prev, [type]: false }));
    setEditingItem(null);
  };

  // const addItem = (type, item) => {
  //   let updatedBookingData;

  //   if (editingItem) {
  //     updatedBookingData = {
  //       ...bookingData,
  //       [type]: bookingData[type].map(existingItem =>
  //         existingItem.id === editingItem.id ? { ...item, id: editingItem.id } : existingItem
  //       )
  //     };
  //     setEditingItem(null);
  //   } else {
  //     const newItem = { ...item, id: nextId[type] };
  //     updatedBookingData = {
  //       ...bookingData,
  //       [type]: [...bookingData[type], newItem]
  //     };
  //     setNextId(prev => ({ ...prev, [type]: prev[type] + 1 }));
  //   }

  //   setBookingData(updatedBookingData);

  //   // Auto-save to localStorage
  //   saveBookingToStorage(updatedBookingData);
  // };

  const addItem = (type, item) => {
    let updatedBookingData;

    if (editingItem) {
      updatedBookingData = {
        ...bookingData,
        [type]: bookingData[type].map(existingItem =>
          existingItem.id === editingItem.id ? { ...item, id: editingItem.id } : existingItem
        )
      };
      setEditingItem(null);
    } else {
      const newItem = { ...item, id: nextId[type] };
      updatedBookingData = {
        ...bookingData,
        [type]: [...bookingData[type], newItem]
      };
      setNextId(prev => ({ ...prev, [type]: prev[type] + 1 }));
    }

    setBookingData(updatedBookingData);

    // 🔥 Save complete booking with updated data
    saveCompleteBooking(updatedBookingData);
  };


  const saveBookingToStorage = (bookingDataToSave) => {
    const bookingRecord = {
      id: customerData.id,
      customer: customerData,
      bookings: bookingDataToSave,
      timestamp: new Date().toISOString(),
      totalItems: Object.values(bookingDataToSave).flat().length,
      lastUpdated: new Date().toISOString()
    };

    const existingBookings = JSON.parse(localStorage.getItem('flightsBookings') || '[]');
    const existingIndex = existingBookings.findIndex(booking => booking.id === customerData.id);

    if (existingIndex >= 0) {
      // Update existing booking
      existingBookings[existingIndex] = bookingRecord;
    } else {
      // Add new booking
      existingBookings.unshift(bookingRecord);
    }

    localStorage.setItem('flightsBookings', JSON.stringify(existingBookings));
  };

  const editItem = (type, item) => {
    setEditingItem(item);
    openModal(type);
  };

  const openDeleteModal = (type, itemId, itemName) => {
    setDeleteModal({ isOpen: true, type, itemId, itemName });
  };

  const closeDeleteModal = () => {
    setDeleteModal({ isOpen: false, type: null, itemId: null, itemName: '' });
  };

  const confirmDelete = () => {
    const { type, itemId } = deleteModal;
    setBookingData(prev => ({
      ...prev,
      [type]: prev[type].filter(item => item.id !== itemId)
    }));
    closeDeleteModal();
  };

  const getTotalItems = () => {
    return Object.values(bookingData).flat().length;
  };

  const handleCompleteBooking = () => {
    if (getTotalItems() === 0) {
      setAlertModal({ isOpen: true, title: 'Empty Booking', message: 'Please add at least one service to the booking' });
      return;
    }

    if (!customerData.name || !customerData.email) {
      setAlertModal({ isOpen: true, title: 'Missing Information', message: 'Please fill in customer name and email' });
      return;
    }

    // Mark booking as completed
    const bookingRecord = {
      id: customerData.id,
      customer: customerData,
      bookings: bookingData,
      timestamp: new Date().toISOString(),
      totalItems: getTotalItems(),
      status: 'completed',
      lastUpdated: new Date().toISOString()
    };

    const existingBookings = JSON.parse(localStorage.getItem('flightsBookings') || '[]');
    const existingIndex = existingBookings.findIndex(booking => booking.id === customerData.id);

    if (existingIndex >= 0) {
      existingBookings[existingIndex] = bookingRecord;
    } else {
      existingBookings.unshift(bookingRecord);
    }

    localStorage.setItem('flightsBookings', JSON.stringify(existingBookings));

    // Show success message
    setAlertModal({
      isOpen: true,
      title: 'Booking Completed!',
      message: `Booking for ${customerData.name} has been completed successfully. You can view it in the booking list.`
    });

    // Navigate to booking list after a short delay
    setTimeout(() => {
      navigate('/admin/booking');
    }, 2000);
  };

  const saveCompleteBooking = (updatedBookingData) => {
    // if (getTotalItems(updatedBookingData) === 0) {
    //   setAlertModal({ isOpen: true, title: 'Empty Booking', message: 'Please add at least one service to the booking' });
    //   return false;
    // }

    if (!customerData.name || !customerData.email) {
      setAlertModal({ isOpen: true, title: 'Missing Information', message: 'Please fill in customer name and email' });
      return false;
    }

    const bookingRecord = {
      id: customerData.id,
      customer: customerData,
      bookings: updatedBookingData,
      timestamp: new Date().toISOString(),
      totalItems: getTotalItems(updatedBookingData),
      status: 'completed',
      lastUpdated: new Date().toISOString()
    };

    const existingBookings = JSON.parse(localStorage.getItem('flightsBookings') || '[]');
    const existingIndex = existingBookings.findIndex(b => b.id === customerData.id);

    if (existingIndex >= 0) {
      existingBookings[existingIndex] = bookingRecord;
    } else {
      existingBookings.unshift(bookingRecord);
    }

    localStorage.setItem('flightsBookings', JSON.stringify(existingBookings));

    return true;
  };


  const handleSaveBooking = () => {
    saveBookingToStorage(bookingData);
  };

  return (
    <div className="space-y-6 ">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-card border border-secondary-100 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            {bookingId && (
              <button
                onClick={() => navigate('/admin/booking')}
                className="flex items-center space-x-2 text-secondary-600 hover:text-secondary-900 transition-colors"
              >
                <FaArrowLeft className="h-4 w-4" />
                <span>Back to Bookings</span>
              </button>
            )}
            <div>
              <h1 className="text-2xl font-display font-bold text-secondary-900">
                {bookingId ? 'Edit Booking' : 'New Booking'}
              </h1>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            {bookingId ? (
              <div className="bg-primary-50 text-primary-700 px-4 py-2 rounded-lg border border-primary-200">
                <span className="text-sm font-medium">ID: {customerData.id}</span>
              </div>
            ) : (
              <div className="bg-green-50 text-green-700 px-4 py-2 rounded-lg border border-green-200">
                <span className="text-sm font-medium">New Booking - ID: {customerData.id}</span>
              </div>
            )}
          </div>
        </div>

        {/* Customer Information */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              <FaUser className="inline mr-2" />
              Customer Name
            </label>
            {!bookingId ? (
              <div className="relative">
                <button
                  onClick={() => setShowCustomerDropdown(!showCustomerDropdown)}
                  className="w-full flex items-center justify-between px-4 py-3 border border-secondary-300 rounded-lg bg-white hover:border-secondary-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                >
                  <span className={selectedCustomer === 'Select Customer' ? 'text-secondary-400' : 'text-secondary-900'}>
                    {selectedCustomer}
                  </span>
                  <FaChevronDown className="h-4 w-4 text-secondary-400" />
                </button>

                {showCustomerDropdown && (
                  <div className="absolute z-10 mt-1 w-full bg-white border border-secondary-200 rounded-lg shadow-business">
                    <button
                      onClick={() => handleCustomerSelect('new')}
                      className="w-full flex items-center px-4 py-3 hover:bg-secondary-50 transition-colors border-b border-secondary-100"
                    >
                      <div className="h-10 w-10 bg-gradient-to-r from-green-500 to-green-600 rounded-full flex items-center justify-center mr-3">
                        <FaPlus className="h-4 w-4 text-white" />
                      </div>
                      <div className="text-left">
                        <p className="font-medium text-secondary-900">New Customer</p>
                        <p className="text-sm text-secondary-500">Create new customer profile</p>
                      </div>
                    </button>
                    {customers.map((customer) => (
                      <button
                        key={customer.id}
                        onClick={() => handleCustomerSelect(customer)}
                        className="w-full flex items-center px-4 py-3 hover:bg-secondary-50 transition-colors"
                      >
                        <div className="h-10 w-10 bg-gradient-to-r from-primary-500 to-primary-600 rounded-full flex items-center justify-center mr-3">
                          <span className="text-white font-medium text-sm">
                            {customer.name.split(' ').map(n => n[0]).join('')}
                          </span>
                        </div>
                        <div className="text-left">
                          <p className="font-medium text-secondary-900">{customer.name}</p>
                          <p className="text-sm text-secondary-500">{customer.id} • {customer.email}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                {selectedCustomer === 'New Customer' && (
                  <input
                    type="text"
                    value={customerData.name}
                    onChange={(e) => setCustomerData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Enter customer name"
                    className="mt-3 input-field"
                  />
                )}
              </div>
            ) : (
              <p className="text-lg font-medium text-secondary-900">{customerData.name}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              <FaEnvelope className="inline mr-2" />
              Email Address
            </label>
            {!bookingId && selectedCustomer === 'New Customer' ? (
              <input
                type="email"
                value={customerData.email}
                onChange={(e) => setCustomerData(prev => ({ ...prev, email: e.target.value }))}
                placeholder="Enter email address"
                className="input-field"
              />
            ) : (
              <p className="text-lg font-medium text-secondary-900">{customerData.email}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              <FaPhone className="inline mr-2" />
              Phone Number
            </label>
            {!bookingId && selectedCustomer === 'New Customer' ? (
              <input
                type="tel"
                value={customerData.phone}
                onChange={(e) => setCustomerData(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="Enter phone number"
                className="input-field"
              />
            ) : (
              <p className="text-lg font-medium text-secondary-900">{customerData.phone}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              <FaMapMarkerAlt className="inline mr-2" />
              Address
            </label>
            {!bookingId && selectedCustomer === 'New Customer' ? (
              <input
                type="text"
                value={customerData.address}
                onChange={(e) => setCustomerData(prev => ({ ...prev, address: e.target.value }))}
                placeholder="Enter address"
                className="input-field"
              />
            ) : (
              <p className="text-lg font-medium text-secondary-900">{customerData.address}</p>
            )}
          </div>
        </div>
      </div>

      {/* Action Bar */}
      {/* <div className="flex justify-between items-center">
        <button
          onClick={handleCompleteBooking}
          className="flex items-center space-x-2 bg-gradient-to-r from-green-600 to-green-700 text-white px-6 py-3 rounded-lg hover:from-green-700 hover:to-green-800 transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={getTotalItems() === 0}
        >
          <FaCheckCircle className="h-5 w-5" />
          <span>Complete Booking ({getTotalItems()})</span>
        </button>

        {getTotalItems() > 0 && (
          <div className="text-sm text-secondary-600">
            Total Services: {Object.entries(bookingData).filter(([_, items]) => items.length > 0).map(([type, items]) => `${items.length} ${type}`).join(', ')}
          </div>
        )}
      </div> */}

      {/* Booking Sections */}
      {/* <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {bookingSections.map((section) => {
          const Icon = section.icon;
          const isExpanded = expandedSection === section.id;
          
          return (
            <div key={section.id} className="bg-white rounded-xl shadow-card border border-secondary-100 overflow-hidden">
              <div
                className={`${section.bgColor} ${section.borderColor} border-b p-6 cursor-pointer hover:bg-opacity-80 transition-colors`}
                onClick={() => setExpandedSection(isExpanded ? null : section.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className={`bg-gradient-to-r ${section.color} p-3 rounded-lg shadow-md`}>
                      <Icon className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-display font-semibold text-secondary-900">
                        {section.title}
                      </h3>
                      <p className="text-sm text-secondary-600">
                        {section.items.length} service{section.items.length !== 1 ? 's' : ''} added
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openModal(section.id);
                      }}
                      className="bg-white text-secondary-700 p-2 rounded-lg hover:bg-secondary-50 transition-colors shadow-sm"
                    >
                      <FaPlus className="h-4 w-4" />
                    </button>
                    {isExpanded ? (
                      <FaChevronUp className="h-5 w-5 text-secondary-400" />
                    ) : (
                      <FaChevronDown className="h-5 w-5 text-secondary-400" />
                    )}
                  </div>
                </div>
              </div>

              {isExpanded && (
                <div className="p-6">
                  {section.items.length === 0 ? (
                    <div className="text-center py-8">
                      <Icon className="h-12 w-12 text-secondary-300 mx-auto mb-4" />
                      <p className="text-secondary-500">No {section.title.toLowerCase()} added yet</p>
                      <button
                        onClick={() => openModal(section.id)}
                        className="mt-3 text-primary-600 hover:text-primary-700 font-medium"
                      >
                        Add {section.title.split(' ')[0]}
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {section.items.map((item, index) => (
                        <div key={index} className={`${section.bgColor} ${section.borderColor} border rounded-lg p-4 group hover:shadow-md transition-shadow`}>
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <h4 className="font-semibold text-secondary-900 mb-2">{item.name}</h4>
                              <div className="grid grid-cols-2 gap-2 text-sm">
                                {Object.entries(item).filter(([key]) => key !== 'name' && key !== 'id').map(([key, value]) => (
                                  <div key={key}>
                                    <span className="text-secondary-500 capitalize">{key}: </span>
                                    <span className="font-medium text-secondary-700">{value}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                            <div className="flex space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button 
                                onClick={() => editItem(section.id, item)}
                                className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                              >
                                <FaEdit className="h-4 w-4" />
                              </button>
                              <button 
                                onClick={() => openDeleteModal(section.id, item.id, item.name)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              >
                                <FaTrash className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div> */}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {bookingSections.map((section) => {
          const Icon = section.icon;
          const isSelected = expandedSection === section.id;
          const isDisabled = !isEmployeeSelected;

          return (
            <div
              key={section.id}
              className={`bg-white rounded-xl shadow-md border border-gray-200 transition ${
                isDisabled 
                  ? 'opacity-50 cursor-not-allowed' 
                  : 'hover:shadow-lg cursor-pointer'
              }`}
              onClick={() => {
                if (!isDisabled) {
                  setExpandedSection(isSelected ? null : section.id);
                }
              }}
            >
              {/* Card */}
              <div className="flex items-center justify-between p-5">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-lg bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow">
                    <Icon className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold">{section.title}</h3>
                    <p className="text-sm text-gray-500">
                      {section.items.length}{" "}
                      {section.items.length === 1 ? "service" : "services"}
                    </p>
                  </div>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    openModal(section.id);
                  }}
                  className={`bg-white border p-2 rounded-lg shadow-sm ${
                    isDisabled 
                      ? 'cursor-not-allowed' 
                      : 'hover:bg-gray-100'
                  }`}
                  disabled={isDisabled}
                >
                  <FaPlus className="h-4 w-4 text-gray-600" />
                </button>
              </div>
            </div>
          );
        })}

        {/* Right Panel for List */}
        {expandedSection && isEmployeeSelected && (
          <div className="fixed top-0 right-0 w-96 h-full bg-white border-l border-gray-200 shadow-2xl z-50 flex flex-col">
            {/* Panel Header */}
            <div className="flex items-center justify-between p-5 border-b">
              <h2 className="text-lg font-semibold">
                {
                  bookingSections.find((s) => s.id === expandedSection)
                    ?.title
                }{" "}
                List
              </h2>
              <button
                onClick={() => setExpandedSection(null)}
                className="p-2 rounded hover:bg-gray-100"
              >
                ✕
              </button>
            </div>

            {/* Panel Content */}
            <div className="flex-1 overflow-y-auto p-5">
              {(() => {
                const section = bookingSections.find(
                  (s) => s.id === expandedSection
                );
                if (!section) return null;

                return section.items.length === 0 ? (
                  <div className="text-center text-gray-500 mt-10">
                    <section.icon className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                    <p>No {section.title.toLowerCase()} added yet</p>
                    <button
                      onClick={() => openModal(section.id)}
                      className="mt-3 text-indigo-600 hover:text-indigo-700 font-medium"
                    >
                      Add {section.title.split(" ")[0]}
                    </button>
                  </div>
                ) : (
                  <ul className="space-y-4">
                    {section.items.map((item, index) => (
                      <li
                        key={index}
                        className="flex justify-between items-start border p-3 rounded-lg hover:shadow transition"
                      >
                        <div>
                          <h4 className="font-semibold">{item.name}</h4>
                          <p className="text-sm text-gray-500">
                            {Object.entries(item)
                              .filter(([k]) => k !== "name" && k !== "id")
                              .map(([k, v]) => `${k}: ${v}`)
                              .join(" • ")}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => editItem(section.id, item)}
                            className="p-2 rounded hover:bg-indigo-50 text-indigo-600"
                          >
                            <FaEdit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() =>
                              openDeleteModal(section.id, item.id, item.name)
                            }
                            className="p-2 rounded hover:bg-red-50 text-red-600"
                          >
                            <FaTrash className="h-4 w-4" />
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                );
              })()}
            </div>
          </div>
        )}
      </div>
      

      {/* Drawer removed as per request; improved inline accordion only */}


      {/* Modals */}
      <FlightModal
        isOpen={modals.flights}
        onClose={() => closeModal('flights')}
        onAdd={(item) => addItem('flights', item)}
        editingItem={editingItem}
      />
      <HotelModal
        isOpen={modals.hotels}
        onClose={() => closeModal('hotels')}
        onAdd={(item) => addItem('hotels', item)}
        editingItem={editingItem}
      />
      <TrainModal
        isOpen={modals.trains}
        onClose={() => closeModal('trains')}
        onAdd={(item) => addItem('trains', item)}
        editingItem={editingItem}
      />
      <CarModal
        isOpen={modals.cars}
        onClose={() => closeModal('cars')}
        onAdd={(item) => addItem('cars', item)}
        editingItem={editingItem}
      />
      <VisaModal
        isOpen={modals.visas}
        onClose={() => closeModal('visas')}
        onAdd={(item) => addItem('visas', item)}
        editingItem={editingItem}
      />

      <DeleteModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={confirmDelete}
        itemName={deleteModal.itemName}
      />

      {/* Alert Modal */}
      {alertModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-md p-4">
          <div className="bg-white rounded-xl shadow-business max-w-md w-full">
            <div className="bg-gradient-to-r from-primary-600 to-primary-700 p-6 rounded-t-xl">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-display font-bold text-white">{alertModal.title}</h3>
                <button
                  onClick={() => setAlertModal({ isOpen: false, title: '', message: '' })}
                  className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition-colors"
                >
                  <FaTimes className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="p-6">
              <p className="text-secondary-700 mb-6">{alertModal.message}</p>

              <button
                onClick={() => setAlertModal({ isOpen: false, title: '', message: '' })}
                className="w-full bg-gradient-to-r from-primary-600 to-primary-700 text-white py-3 px-6 rounded-lg font-semibold hover:from-primary-700 hover:to-primary-800 transition-all shadow-md hover:shadow-lg"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingManagement;